import find from 'lodash/find';
import pick from 'lodash/pick';
import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';
import merge from 'lodash/merge';
import values from 'lodash/values';
import last from 'lodash/last';
import uniqBy from 'lodash/uniqBy';
import flatten from 'lodash/flatten';
import map from 'lodash/map';
import partition from 'lodash/partition';
import sortBy from 'lodash/sortBy';
import moment from 'moment';
import { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { CrewType } from '@greywing-maritime/frontend-library/dist/types/crewChangeEventTypes';
import type { AirportCommon } from '@greywing-maritime/frontend-library/dist/types/airports';

import { showToaster } from 'lib/toaster';
import { COUNTRIES_LIST, getCountryFromAlpha3 } from 'lib/countries';
import { generateRandomNumId } from 'lib/common';
import { arrayMoveImmutable } from 'utils/array-move';
import { isHistoricalEvent } from 'utils/crew-change-events';
import { formatDate } from 'utils/format-date';
import { Crew, OrgCrew } from 'utils/types/crew-change-types';
import {
  AirportOption,
  CCPanelInfo,
  ColumnVisibility,
  CountryOption as Country,
} from 'redux/types';
import { INVALID_DATE } from './constants';
import { CrewInput, CustomCrew, ListItemCrew, RemoveCrewFunc } from '../types';

// save list from crew table
export let CREW_ROWS: CrewInput[] = [];

const generateReplacement = (crew: Crew, type: CrewType, index?: number) => {
  const { id: replacementCrewId } = crew;
  const id = Date.now() + (index || 0);
  return {
    id,
    type,
    name: `Missing ${type}`,
    replacementCrewId,
  };
};

// insert missing fields to an added crew (either user input or org crew)
export const addMissingFields = (crew: CrewInput) => {
  const { sex } = crew;
  return {
    ...crew,
    ...getCrewTravelInfo(crew),
    sex: (sex === 'F' && 'Female') || (sex === 'M' && 'Male') || '',
  };
};

// pair crew with replacements to get `onsigner-offsigner` combination
export const pairCrewTypes = (list: Crew[]) => {
  const sortedList = Array.from(list).sort((a, b) => a.id - b.id);
  return sortedList.reduce((acc, _, index) => {
    const { id: crewId, type, replacementCrewId } = acc[index];
    // find if available `replacementCrewId` is adjacent to current one
    const hasAdjacentReplacement =
      !replacementCrewId ||
      replacementCrewId === acc[index + 1]?.id ||
      replacementCrewId === acc[index - 1]?.id;

    // if so, return current list
    if (hasAdjacentReplacement) {
      return acc;
    }

    // if not, find where the replacement is in the list
    // & move it adjacent to current crew
    const fromIndex = acc.findIndex((c) => replacementCrewId === c.id);
    const currentIndex = acc.findIndex((c) => crewId === c.id);
    const toIndex = type === 'onsigner' ? currentIndex + 1 : currentIndex - 1;
    return arrayMoveImmutable(acc, fromIndex, toIndex);
  }, sortedList);
};

// add missing replacements in the crew pairs
export const addReplacements = (crewList: Crew[] = []) => {
  let newCrewList: (Crew | CustomCrew)[] = [];

  pairCrewTypes(crewList).forEach((c, i) => {
    if (!c.replacementCrewId) {
      // add unique custom id (number) for replacement rows in table
      if (c.type === 'onsigner') {
        newCrewList = [
          ...newCrewList,
          c,
          generateReplacement(c, CrewType.offsigner!, i),
        ];
      } else {
        newCrewList = [
          ...newCrewList,
          generateReplacement(c, CrewType.onsigner!, i),
          c,
        ];
      }
      return;
    }
    newCrewList.push(c);
  });

  return newCrewList;
};

// util to find initial crew list for crew table in cc panel
// merges lists - `crew` from `planningData` & `crew` in `event`
export const mergeCrewList = (
  data: Crew[],
  crewList: (CrewInput | CustomCrew)[]
) => {
  const [addedIndependent, remaining] = partition(
    data,
    ({ added, replacementCrewId }) => added && !replacementCrewId
  );
  const formattedPairedCrewList =
    remaining.length >= crewList.length
      ? remaining.map(
          (selected) =>
            crewList.find(({ id }) => id === selected.id) || selected
        )
      : crewList.map(
          (eventCrew) =>
            remaining.find(({ id }) => id === eventCrew.id) || eventCrew
        );

  return [
    ...addedIndependent, // insert added crew that are indepenedent (not replacement)
    ...formattedPairedCrewList,
  ];
};

export const isValidCrew = ({ name }: { name?: string | null }) =>
  !(name && name.toLowerCase().includes('missing'));

export const isAllCrewSelected = (
  crewList: Crew[],
  rows: Crew[] | Partial<Crew>[]
) =>
  Boolean(crewList.length) &&
  crewList.length === rows.filter(isValidCrew).length;

export const hasInvalidSelectedCrew = (crew: Crew[]) => {
  const validCrewList = crew.filter(
    (crew) => isValidCrew(crew) && Boolean(crew.homeAirport?.iataCode)
  );
  return validCrewList.length < crew.length;
};

// determine if a crew table row is selectable
export const isCrewSelectable =
  (crewList: Partial<CrewInput>[]) => (params: GridRowParams) => {
    const {
      id: crewId,
      cid: crewCID,
      name,
      order: rowOrder,
    } = params.row as CrewInput;
    if (!isValidCrew({ name })) return false;

    // find the occurrences of this crew in the table
    const crewOccurrences = crewList.filter(
      ({ id, cid }) => crewId === id || (cid && crewCID && cid === crewCID)
    );
    if (crewOccurrences.length === 2) {
      // check if crew is replacing her/himself
      const isReplacingOwn =
        crewOccurrences[0].type !== crewOccurrences[1].type;
      // make un-selectable in that case
      if (isReplacingOwn) return false;
    }
    // otherwise, make any occurrence other than the first one is un-selectable
    const notFirstOccurrence =
      rowOrder &&
      crewOccurrences[0]?.order &&
      rowOrder > crewOccurrences[0].order;
    return !notFirstOccurrence;
  };

// sort rows in table based on order
export const sortCrewOrder = (crewList: CrewInput[]) => {
  const [ordered, unordered] = partition(
    uniqBy(crewList, 'id'),
    ({ order }) => order
  );
  return [...unordered, ...ordered.sort((a, b) => a.order! - b.order!)];
};

export const sortCrewRank = (crewList: CrewInput[]) => {
  const [offsigners, onsigners] = partition(
    // handle cases of read-only data
    [...crewList].sort((a, b) => (a.rank || '').localeCompare(b.rank || '')),
    ({ type }) => type === CrewType.offsigner
  );
  return [...offsigners, ...onsigners];
};

export const hideCrewPlanning = (ccPanelInfo: CCPanelInfo): boolean => {
  const {
    event,
    active: activity,
    readOnlyPlanningData: reportData,
  } = ccPanelInfo;
  return (
    activity === 'readOnly' ||
    !event ||
    // diregard for historical event if saved report
    (!reportData && isHistoricalEvent(event))
  );
};

export const addToCrewRows = (crewList: CrewInput[], isNew?: boolean) => {
  CREW_ROWS = uniqBy([...crewList, ...(!isNew ? CREW_ROWS : [])], 'id');
  return CREW_ROWS;
};

export const clearStoredCrewList = () => {
  CREW_ROWS = [];
};

// remove either `all` or a specific `crew`
export const removeCrew: RemoveCrewFunc = ({ all, crew }) => {
  if (all) {
    // @ts-ignore - allow minimum replacement default crew
    CREW_ROWS = CREW_ROWS.reduce((acc: CrewInput[], crew, index) => {
      if (!crew.added) return [...acc, crew];
      return crew.replacementCrewId && !crew.existing
        ? // for replacements, add back generated crew
          [
            ...acc,
            {
              ...generateReplacement(crew, crew.type!, index),
              order: crew.order,
            },
          ]
        : acc; // otherwise, remove it from saved memory
    }, []);
  } else {
    // @ts-ignore - allow minimum replacement default crew
    CREW_ROWS = crew?.replacementCrewId && !crew.existing // eslint-disable-line
        ? CREW_ROWS.map((c) =>
            c.id === crew?.id
              ? // uses minimum fields for default replacement crew
                { ...generateReplacement(c, c.type!), order: c.order }
              : c
          )
        : CREW_ROWS.filter(({ id }) => id !== crew?.id);
  }
  return CREW_ROWS;
};

export const getCrewWage = (crew: Crew | CustomCrew) => {
  const { monthlySalary, wage } = crew;
  return {
    amount: wage || (monthlySalary ? Math.round(monthlySalary.wage / 30) : 0),
    currency: monthlySalary?.currency,
  };
};

export const getAutoGeneratedName = () => {
  // added crews will have this property, missing crews won't
  const crewCount = CREW_ROWS.filter((crew) =>
    crew.hasOwnProperty('added')
  ).length;

  // names with pattern `Crew Member ${num}` to be auto-generated, case insensitive
  const autoGeneratedNames = CREW_ROWS.reduce((acc: string[], { name }) => {
    const matched = (name || '').match(/(crew)\s+(member)\s+[0-9]+/gi);
    return matched ? [...acc, matched[0].replace(/\s+/g, ' ')] : acc;
  }, []).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  if (!autoGeneratedNames.length) {
    return `Crew Member ${crewCount + 1}`;
  }

  const lastNumber = last(autoGeneratedNames)?.match(/\d+/g)?.[0];
  return `Crew Member ${(lastNumber ? Number(lastNumber) : crewCount) + 1}`;
};

export const formatCountries = (
  recentSearches: Country[] = [],
  hideRecent: boolean
): Country[] => {
  // don't show recent searches if input has query/text
  const recentCountries = !hideRecent
    ? recentSearches.map((country) => ({
        ...country,
        label: 'Recently Used',
      }))
    : [];
  const defaultList = COUNTRIES_LIST
    // filter out recent searches/countries out of the regular list to avoid duplication
    .filter(({ name }) => !recentCountries.some((c) => c.name === name));

  return filter(
    [
      ...recentCountries,
      ...defaultList.map((country) => ({
        ...country,
        ...(recentCountries.length ? { label: 'Countries' } : {}),
      })),
    ],
    'countryCode'
  );
};

// get the initial columns to be visible in a table
export const getCrewInitialColumnVisibility = (columns: GridColDef[]) => {
  const defaultDisabled: string[] = [
    'SB No.',
    'SB Issue',
    'SB Expiry',
    'PP Issue',
    'Visa Country',
    'Visa Expiry',
  ];
  const fixedColumns: string[] = [
    'CID',
    'Type',
    'Name',
    'Rank',
    'Country',
    'Home Airport',
  ];
  return columns.map(
    ({ field, headerName = '' }) => ({
      field,
      name: headerName,
      selected: !defaultDisabled.includes(headerName),
      fixed: fixedColumns.includes(headerName),
    }),
    [] as ColumnVisibility[]
  );
};

// get latest & valid visa details for crew
export const getLatestCrewVisa = (crew: Crew | CrewInput | CustomCrew) => {
  if ((crew as CrewInput).visaCountry) {
    const { visaCountry, visaExpiry } = crew as CrewInput;
    return { visaCountry, visaExpiry };
  }

  const { visas: visaDetails = [] } = crew;
  const visaInfo = find(
    sortBy(visaDetails, 'expiryDate'),
    ({ hasVisa, expiryDate }) =>
      Boolean(hasVisa && expiryDate && moment(expiryDate).isAfter(moment()))
  );
  return {
    visaCountry: visaInfo?.countryIso3,
    visaExpiry: visaInfo?.expiryDate
      ? formatDate(visaInfo.expiryDate, 'DD/MM/YYYY')
      : undefined,
  };
};

// get passport, seaman book & valid visa details for crew
const getCrewTravelInfo = (crew: Crew | CustomCrew) => {
  return {
    ...pick(crew, [
      'passportNumber',
      'passportExpiry',
      'passportIssued',
      'seamenBookNumber',
      'seamenBookIssued',
      'seamenBookExpiry',
    ]),
    ...getLatestCrewVisa(crew),
  };
};

const getRows = (crew: (Crew | CustomCrew)[]) =>
  crew.map((member, index) => {
    const sex =
      (member.sex === 'M' && 'Male') || (member.sex === 'F' && 'Female') || '';
    return isValidCrew(member)
      ? {
          id: member.id, // add a unique id
          type: member.type,
          name: member.name,
          birthday: member.birthday,
          manningOffice: member.manningOffice,
          country: member.country,
          added: !!member.added,
          replacementCrewId: member.replacementCrewId,
          homeAirport: member.homeAirport,
          wage: getCrewWage(member).amount,
          rank: member.rank,
          monthlySalary: member.monthlySalary,
          sex,
          ...getCrewTravelInfo(member),
          ...(!member.added ? { cid: member.cid, order: index + 1 } : {}),
        }
      : {
          id: member.id,
          type: member.type,
          name: member.name,
          country: member.country,
          rank: member.rank,
          order: index + 1,
          replacementCrewId: member.replacementCrewId,
          homeAirport: null,
          sex,
          wage: 0,
        };
  });

export const getCrewRows = (data: Crew[], crewList?: Crew[]) =>
  (CREW_ROWS.length && CREW_ROWS) ||
  getRows(mergeCrewList(data, addReplacements(crewList))) ||
  [];

export const getCrewRowClassName =
  (addedId?: number | boolean) =>
  ({ row }: { row: CrewInput }) => {
    const { id, added, order, replacementCrewId } = row;

    if (added) {
      return replacementCrewId
        ? `added-replacement ${(order || 0) % 2 ? 'odd' : 'even'}`
        : 'added-crew';
    }

    const rowState = addedId === id ? 'adding-crew' : 'existing-crew';
    return `${rowState} ${(order || 0) % 2 ? 'odd' : 'even'}`;
  };

export const isValidCrewUpdate = (newRow: CrewInput, oldRow: CrewInput) => {
  switch (true) {
    case newRow.birthday !== oldRow.birthday: {
      if (newRow.birthday === INVALID_DATE) {
        if (!oldRow.birthday) return;
        return showToaster({
          message: 'Cannot be updated. Invalid crew birthday.',
          type: 'error',
        });
      }
      return true;
    }

    case newRow.passportIssued !== oldRow.passportIssued: {
      if (newRow.passportIssued === INVALID_DATE) {
        if (!oldRow.passportIssued) return;
        return showToaster({
          message: 'Cannot be updated. Invalid crew passport issued date.',
          type: 'error',
        });
      }
      return true;
    }

    case newRow.passportExpiry !== oldRow.passportExpiry: {
      if (newRow.passportExpiry === INVALID_DATE) {
        if (!oldRow.passportExpiry) return;
        return showToaster({
          message: 'Cannot be updated. Invalid crew passport expiry date.',
          type: 'error',
        });
      }
      return true;
    }

    case newRow.seamenBookIssued !== oldRow.seamenBookIssued: {
      if (newRow.seamenBookIssued === INVALID_DATE) {
        if (!oldRow.seamenBookIssued) return;
        return showToaster({
          message: 'Cannot be updated. Invalid crew seaman book issued date.',
          type: 'error',
        });
      }
      return true;
    }

    case newRow.seamenBookExpiry !== oldRow.seamenBookExpiry: {
      if (newRow.seamenBookExpiry === INVALID_DATE) {
        if (!oldRow.seamenBookExpiry) return;
        return showToaster({
          message: 'Cannot be updated. Invalid crew seaman book expiry date.',
          type: 'error',
        });
      }
      return true;
    }

    case newRow.visaExpiry !== oldRow.visaExpiry: {
      if (newRow.visaExpiry === INVALID_DATE) {
        if (!oldRow.visaExpiry) return;
        return showToaster({
          message: 'Cannot be updated. Invalid crew visa expiry date.',
          type: 'error',
        });
      }
      return true;
    }

    default:
      return true;
  }
};

export const formatCrewHomeAirport = (airport?: AirportCommon | null) => {
  if (!airport) {
    return '';
  }
  const { iataCode, name, municipality } = airport;
  const baseName = name ? `${iataCode}-${name}` : iataCode;
  return municipality ? `${baseName}, ${municipality}` : baseName;
};

// format the list of airport options
export const getAirportOptions = (
  recentAirports: AirportOption[],
  airports: AirportCommon[],
  hideRecent: boolean
) => {
  // don't show recent search items when input has query/text
  if (hideRecent) return airports;

  const recentItems = recentAirports.map((airport) => ({
    ...airport,
    label: 'Recently Used',
  }));
  return [
    ...recentItems,
    ...(airports || []).reduce(
      (acc: AirportOption[], airport: AirportCommon) => {
        const formattedName = formatCrewHomeAirport(airport);
        const alreadySearched = recentItems.some(
          (a) => a.text === formattedName
        );
        return alreadySearched
          ? acc
          : [
              ...acc,
              {
                ...airport,
                ...(recentItems.length ? { label: 'Airports' } : {}),
              },
            ];
      },
      []
    ),
  ];
};

// formats the list item of selection crew list - containing event & org crew
export const formatListCrewItem = (crew: ListItemCrew) => {
  const { index, rank, name, country, homeAirport } = crew;
  const { iataCode, municipality } = homeAirport || {};
  const crewDetails = `${name || `Unknown ${index || 0 + 1}`} ${
    rank ? `(${rank})` : ''
  } ${country ? `- ${country}` : ''}`.trim();
  const airport =
    iataCode && municipality
      ? `${iataCode}, ${municipality}`
      : `${iataCode || ''}`;
  return airport ? `${crewDetails} - ${airport}` : `${crewDetails}`;
};

// insert id & other fields to org list crew items
const formatOrgCrew = (crewList: OrgCrew[]) =>
  map(crewList, (crew) => ({
    ...crew,
    id: generateRandomNumId(),
    country:
      crew.country ||
      // add country field from `nationalityIsoCode3` field
      (crew.nationalityIsoCode3
        ? getCountryFromAlpha3(crew.nationalityIsoCode3)?.name
        : null),
    homeAirport:
      crew.homeAirport ||
      // add custom airport object if not available
      (crew.homeAirportIataCode
        ? {
            iataCode: crew.homeAirportIataCode,
            name: '',
            text: '',
            municipality: null,
            lat: null,
            lon: null,
          }
        : null),
  }));

// for an empty/fresh event -
// generate crew-list to select from, prepared from other event crews
// & crews coming from back-end from UniSea
export const getVesselEventsCrewList =
  (eventsCrew: Crew[], activeCrewList: CrewInput[]) =>
  (orgCrew: OrgCrew[] = []) => {
    // merged list of org & event crew - combines similar crew to avoid duplication
    const mergedCrewList = values(
      merge(keyBy(formatOrgCrew(orgCrew), 'cid'), keyBy(eventsCrew, 'cid'))
    );
    const allCrewList = uniqBy(mergedCrewList, formatListCrewItem)
      // filter out crew that are in table & insert index
      .reduce<{
        onsigner: ListItemCrew[];
        offsigner: ListItemCrew[];
        unspecified: ListItemCrew[];
      }>(
        (acc, crew, index) => {
          const alreadySelected = activeCrewList.some(
            ({ id }) => crew.id === id
          );
          if (alreadySelected) return acc;
          const label = crew.type || 'unspecified';
          // calculate daily wage
          const wageDetails = crew.monthlySalary?.wage
            ? { wage: Math.round(crew.monthlySalary.wage / 30) }
            : {};
          return {
            ...acc,
            [label]: [
              ...acc[label],
              {
                ...crew,
                ...wageDetails,
                index,
                label,
              },
            ],
          };
        },
        {
          onsigner: [],
          offsigner: [],
          unspecified: [],
        }
      );
    // Order crew-list items as required - offsigner first. Then sort by name inside each group
    // Finally, return flattened list to show in the dropdown
    return flatten(
      map(
        [allCrewList.offsigner, allCrewList.onsigner, allCrewList.unspecified],
        (list) => sortBy(list, 'name')
      )
    );
  };

// updates already fetched org crew types
// when a different vessel is selected for crew-change panel
export const calculateOrgCrewTypes = (
  crewList: OrgCrew[],
  vesselIMO?: string
): OrgCrew[] =>
  crewList.map((crew) => {
    const { currentVesselImo } = crew;
    const crewTypeObj =
      (currentVesselImo &&
        vesselIMO && {
          type:
            currentVesselImo === vesselIMO
              ? CrewType.offsigner
              : CrewType.onsigner,
        }) ||
      {};

    return { ...crew, ...crewTypeObj };
  });
